import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Input } from "../../../components/ui/input";
import { Button } from "../../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../../components/ui/dialog";
import { Label } from "../../../components/ui/label";
import {
  useControlsWithSessions,
  useCreateControlSession,
  SessionStatusFilter,
  DryControlSession,
} from "../../../hooks/useControlsV2";
import { FilterOptions } from "../../../models/audit/control";
import { fetchFastApi } from "../../../api/fastapi";
import { toast } from "sonner";
import { DeleteSessionDialog } from "../../../components/audit/DeleteSessionDialog";
import { useDebounce } from "../../../hooks/useDebounce";
import { FieldworkFilterBar } from "./components/FieldworkFilterBar";
import { FieldworkTable } from "./components/FieldworkTable";
import { OptroAccessListNotice } from "../../../components/audit/optroAccess/OptroAccessListNotice";
import { StatsStrip } from "./components/StatsStrip";
import {
  TestPhase,
  TestPhases,
  testPhaseValues,
} from "../../../models/audit/controlSession";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import { CreatableCombobox } from "../../../components/ui/creatable-combobox";
import { useFyCycleField } from "../../../hooks/useFyCycleField";
import { SetDefaultFyCycleDialog } from "../../../components/audit/SetDefaultFyCycleDialog";

export function FieldworkPage({
  filterOptions,
}: {
  filterOptions: FilterOptions | null;
}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(
    searchParams.get("search") || "",
  );
  const debouncedSearch = useDebounce(searchTerm, 500);
  const [cycleFilters, setCycleFilters] = useState<string[]>(
    searchParams.getAll("cycle").filter(Boolean),
  );
  const [processFilters, setProcessFilters] = useState<string[]>(
    searchParams.getAll("process").filter(Boolean),
  );
  const [controlOwnerFilters, setControlOwnerFilters] = useState<string[]>(
    searchParams.getAll("controlOwner").filter(Boolean),
  );
  const [sortBy, setSortBy] = useState<string>(
    searchParams.get("sortBy") || "",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sortOrder") as "asc" | "desc") || "desc",
  );
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [sessionStatusFilters, setSessionStatusFilters] = useState<
    SessionStatusFilter[]
  >(
    searchParams
      .getAll("sessionStatus")
      .filter(
        (v): v is SessionStatusFilter =>
          v === "in_progress" || v === "in_review" || v === "complete",
      ),
  );

  // Create session dialog
  const [createSessionControlId, setCreateSessionControlId] = useState<
    string | null
  >(null);
  const [testPhaseFilters, setTestPhaseFilters] = useState<TestPhase[]>(
    searchParams
      .getAll("testPhase")
      .filter((v): v is TestPhase => TestPhases.some((tp) => tp.value === v)),
  );
  const [testCreatorFilters, setTestCreatorFilters] = useState<string[]>(
    searchParams.getAll("testCreator").filter(Boolean),
  );
  const [fyCycleIdFilters, setFyCycleIdFilters] = useState<string[]>(
    searchParams.getAll("fyCycleId").filter(Boolean),
  );
  const [newSessionName, setNewSessionName] = useState("New Test");
  const [newSessionTestPhase, setNewSessionTestPhase] =
    useState<TestPhase>("not_defined");
  const { createSession, loading: loadingCreateSession } =
    useCreateControlSession();
  const {
    fyCycle: newSessionFyCycle,
    fyCycleOptionNames,
    pendingDefaultName,
    initWithDefault,
    handleChange: handleFyCycleChange,
    confirmSetDefault,
    dismissSetDefault,
    resolveFyCycleId,
    reset: resetFyCycle,
  } = useFyCycleField();

  const filters = useMemo(
    () => ({
      searchTerm: debouncedSearch || undefined,
      cycles: cycleFilters.length > 0 ? cycleFilters : undefined,
      processes: processFilters.length > 0 ? processFilters : undefined,
      controlOwners:
        controlOwnerFilters.length > 0 ? controlOwnerFilters : undefined,
      sessionStatuses:
        sessionStatusFilters.length > 0 ? sessionStatusFilters : undefined,
      testPhases: testPhaseFilters.length > 0 ? testPhaseFilters : undefined,
      testCreators:
        testCreatorFilters.length > 0 ? testCreatorFilters : undefined,
      fyCycleIds: fyCycleIdFilters.length > 0 ? fyCycleIdFilters : undefined,
      sortBy: sortBy || undefined,
      sortOrder,
    }),
    [
      debouncedSearch,
      cycleFilters,
      processFilters,
      controlOwnerFilters,
      sessionStatusFilters,
      testPhaseFilters,
      testCreatorFilters,
      fyCycleIdFilters,
      sortBy,
      sortOrder,
    ],
  );

  const {
    controls,
    totalCount,
    sessionSummary,
    optroAccessUnavailable,
    loading,
    loadMore,
    hasMore,
    refetch,
  } = useControlsWithSessions(filters);

  // Delete session state
  const [sessionToDelete, setSessionToDelete] =
    useState<DryControlSession | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteSession = (session: DryControlSession) => {
    setSessionToDelete(session);
  };

  const handleConfirmDelete = async () => {
    if (!sessionToDelete) return;
    setIsDeleting(true);
    try {
      await fetchFastApi({
        method: "DELETE",
        path: `/controls/v2/control-sessions/${sessionToDelete.id}`,
      });
      toast.success("Test deleted");
      setSessionToDelete(null);
      refetch();
    } catch (error) {
      console.error("Failed to delete session:", error);
      toast.error("Failed to delete test");
    } finally {
      setIsDeleting(false);
    }
  };

  // Sync filters to URL, preserving any tab param set by the parent.
  useEffect(() => {
    const params = new URLSearchParams();
    const tab = searchParams.get("tab");
    if (tab) params.set("tab", tab);
    if (searchTerm) params.set("search", searchTerm);
    cycleFilters.forEach((v) => params.append("cycle", v));
    processFilters.forEach((v) => params.append("process", v));
    controlOwnerFilters.forEach((v) => params.append("controlOwner", v));
    sessionStatusFilters.forEach((v) => params.append("sessionStatus", v));
    testPhaseFilters.forEach((v) => params.append("testPhase", v));
    testCreatorFilters.forEach((v) => params.append("testCreator", v));
    fyCycleIdFilters.forEach((v) => params.append("fyCycleId", v));
    if (sortBy) params.set("sortBy", sortBy);
    if (sortOrder) params.set("sortOrder", sortOrder);
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [
    searchTerm,
    cycleFilters,
    processFilters,
    controlOwnerFilters,
    sessionStatusFilters,
    testPhaseFilters,
    testCreatorFilters,
    fyCycleIdFilters,
    sortBy,
    sortOrder,
    searchParams,
    setSearchParams,
  ]);

  // Infinite scroll sentinel — disconnect on unmount to prevent leaks on tab switch
  const observer = useRef<IntersectionObserver | null>(null);
  useEffect(() => {
    return () => {
      observer.current?.disconnect();
    };
  }, []);

  const lastRowRef = useCallback(
    (node: HTMLTableRowElement | null) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) loadMore();
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore, loadMore],
  );

  const hasActiveFilters =
    !!searchTerm ||
    cycleFilters.length > 0 ||
    processFilters.length > 0 ||
    controlOwnerFilters.length > 0 ||
    sessionStatusFilters.length > 0 ||
    testPhaseFilters.length > 0 ||
    testCreatorFilters.length > 0 ||
    fyCycleIdFilters.length > 0;

  const activeFilterCount = [
    cycleFilters.length > 0,
    processFilters.length > 0,
    controlOwnerFilters.length > 0,
    sessionStatusFilters.length > 0,
    testPhaseFilters.length > 0,
    testCreatorFilters.length > 0,
    fyCycleIdFilters.length > 0,
  ].filter(Boolean).length;

  const clearFilters = () => {
    setSearchTerm("");
    setCycleFilters([]);
    setProcessFilters([]);
    setControlOwnerFilters([]);
    setSessionStatusFilters([]);
    setTestPhaseFilters([]);
    setTestCreatorFilters([]);
    setFyCycleIdFilters([]);
  };

  const handleCreateSession = async () => {
    if (!createSessionControlId) return;
    try {
      const fyCycleId = await resolveFyCycleId();
      const sessionId = await createSession(
        createSessionControlId,
        newSessionName,
        newSessionTestPhase,
        fyCycleId,
      );
      if (sessionId) {
        setCreateSessionControlId(null);
        setNewSessionName("New Test");
        setNewSessionTestPhase("not_defined");
        resetFyCycle();
        navigate(`/audit/control-sessions/session/${sessionId}`);
      }
    } catch {
      // resolveFyCycleId / createSession have already toasted; keep dialog open.
    }
  };

  return (
    <div className="flex-1 min-w-0 h-full flex flex-col overflow-hidden">
      <StatsStrip
        sessionSummary={sessionSummary}
        totalCount={totalCount}
        sessionStatusFilters={sessionStatusFilters}
        onSessionStatusFiltersChange={setSessionStatusFilters}
      />

      {/* Filter bar */}
      <div className="shrink-0 px-6 py-3 border-b border-stone-200">
        <FieldworkFilterBar
          searchTerm={searchTerm}
          onSearchTermChange={setSearchTerm}
          testPhaseFilters={testPhaseFilters}
          onTestPhaseFiltersChange={setTestPhaseFilters}
          cycleFilters={cycleFilters}
          onCycleFiltersChange={setCycleFilters}
          processFilters={processFilters}
          onProcessFiltersChange={setProcessFilters}
          controlOwnerFilters={controlOwnerFilters}
          onControlOwnerFiltersChange={setControlOwnerFilters}
          sessionStatusFilters={sessionStatusFilters}
          onSessionStatusFiltersChange={setSessionStatusFilters}
          testCreatorFilters={testCreatorFilters}
          onTestCreatorFiltersChange={setTestCreatorFilters}
          fyCycleIdFilters={fyCycleIdFilters}
          onFyCycleIdFiltersChange={setFyCycleIdFilters}
          filterOptions={filterOptions}
          isFiltersOpen={isFiltersOpen}
          onFiltersOpenChange={setIsFiltersOpen}
          activeFilterCount={activeFilterCount}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />
      </div>

      <OptroAccessListNotice
        unavailable={optroAccessUnavailable}
        onRetry={refetch}
        className="mx-6 mt-4 w-auto shrink-0"
      />

      {/* Scrollable table area */}
      <div className="flex-1 overflow-auto px-6 mt-4 pb-16">
        <FieldworkTable
          controls={controls}
          loading={loading}
          hasActiveFilters={hasActiveFilters}
          lastRowRef={lastRowRef}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={(field) => {
            if (sortBy === field) {
              setSortOrder(sortOrder === "asc" ? "desc" : "asc");
            } else {
              setSortBy(field);
              setSortOrder("desc");
            }
          }}
          onCreateSession={async (controlId) => {
            setCreateSessionControlId(controlId);
            setNewSessionName("New Test");
            setNewSessionTestPhase("not_defined");
            await initWithDefault();
          }}
          onDeleteSession={handleDeleteSession}
        />
      </div>

      {/* Create session dialog */}
      <Dialog
        open={Boolean(createSessionControlId)}
        onOpenChange={(open) => {
          if (!open) setCreateSessionControlId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create new test</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Label htmlFor="session-name" className="text-sm font-medium">
              Test name
            </Label>
            <Input
              id="session-name"
              value={newSessionName}
              onChange={(e) => setNewSessionName(e.target.value)}
              className="mt-1.5"
              placeholder="e.g. Q4 2024 Test"
            />
            <label htmlFor="testPhase" className="text-right">
              Test Phase
            </label>
            <Select
              value={newSessionTestPhase}
              onValueChange={(value) => {
                if (testPhaseValues.includes(value as TestPhase)) {
                  setNewSessionTestPhase(value as TestPhase);
                }
              }}
            >
              <SelectTrigger id="testPhase" className="col-span-3">
                <SelectValue placeholder="Select test phase" />
              </SelectTrigger>
              <SelectContent>
                {TestPhases.map((phase: (typeof TestPhases)[number]) => (
                  <SelectItem key={phase.value} value={phase.value}>
                    {phase.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label htmlFor="fyCycle" className="text-sm font-medium mt-2">
              FY
            </Label>
            <CreatableCombobox
              value={newSessionFyCycle}
              onChange={handleFyCycleChange}
              options={fyCycleOptionNames}
              testId="fieldwork-new-session-fy-cycle-combobox"
              placeholder="Select or create FY cycle..."
              emptyText="No FY cycles yet. Type to create one."
              createText="Create"
              modal
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCreateSessionControlId(null)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateSession}
              disabled={!newSessionName.trim() || loadingCreateSession}
            >
              {loadingCreateSession ? "Creating…" : "Create test"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete session dialog */}
      <DeleteSessionDialog
        open={Boolean(sessionToDelete)}
        onOpenChange={(open) => {
          if (!open) setSessionToDelete(null);
        }}
        isDeleting={isDeleting}
        sessionName={sessionToDelete?.name}
        onConfirm={handleConfirmDelete}
      />

      {/* Set Default FY Cycle Dialog */}
      <SetDefaultFyCycleDialog
        open={pendingDefaultName !== null}
        optionName={pendingDefaultName ?? ""}
        onConfirm={confirmSetDefault}
        onDismiss={dismissSetDefault}
      />
    </div>
  );
}
